import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import colors from "../../assets/theme/colors";
import { getTimelineEntriesSnapshot } from "../../services/timeline";
import { useNavigate } from "react-router-dom";
import {
  buildShareText,
} from "./reportBuilders/dailyCareReportBuilder";
import { useDailyCareReportControls } from "./useDailyCareReportControls";
import DailyCareReportFiltersDrawer from "./DailyCareReportFiltersDrawer";
import DailyCareReportHeader from "./DailyCareReportHeader";
import DailyCareReportContent from "./DailyCareReportContent";
import TherapyPrepSourceEntrySheet from "./TherapyPrepSourceEntrySheet";
import {
  copyBrowserText,
  printCurrentBrowserPage,
  shareBrowserText,
} from "../../features/sharing/browserSharing";


const DailyCareReport = ({ child, childId, childName, childAccent, onLogSomething, onBack }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [entries, setEntries] = useState([]);
  const [selectedSourceEntry, setSelectedSourceEntry] = useState(null);
  const {
    rangePreset,
    setRangePreset,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    selectedFilters,
    filterPanelOpen,
    setFilterPanelOpen,
    filteredEntries,
    dateSections,
    summaryData,
    summaryLoading,
    keyEvents,
    questionsToDiscuss,
    peopleLoggedCount,
    periodLabel,
    isCustomRangeInvalid,
    toggleFilter,
    clearFilters,
    selectAllFilters,
  } = useDailyCareReportControls({ entries });

  const shareText = useMemo(
    () =>
      buildShareText({
        childName,
        periodLabel,
        dateSections,
        summaryData,
        keyEvents,
        questions: questionsToDiscuss,
      }),
    [childName, dateSections, keyEvents, periodLabel, questionsToDiscuss, summaryData]
  );

  const loadEntries = useCallback(async () => {
    if (!childId) return;
    setLoading(true);
    setFeedback("");

    try {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const timelineEntries = await getTimelineEntriesSnapshot(childId, {
        startDate: ninetyDaysAgo,
        endDate: new Date(),
      });

      setEntries(timelineEntries);
    } catch (error) {
      console.error("Error loading care team update entries:", error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (childId) {
      loadEntries();
    }
  }, [childId, loadEntries]);

  const handleCopy = async () => {
    try {
      await copyBrowserText({ text: shareText });
      setFeedback("Care team update copied.");
    } catch (error) {
      console.error("Error copying care team update:", error);
      setFeedback("Copy failed. Try again.");
    }
  };

  const handleShare = async () => {
    try {
      const result = await shareBrowserText({
        title: `Care Team Update — ${childName}`,
        text: shareText,
      });
      if (result.method === "native") {
        setFeedback("Care team update shared.");
        return;
      }
      setFeedback("Share is not available here. Copied instead.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Error sharing care team update:", error);
        setFeedback("Share failed. Try again.");
      }
    }
  };

  const handleDownloadPdf = () => {
    setFeedback("Use the print dialog to save the report as a PDF.");
    printCurrentBrowserPage();
  };

  const handleBackToDashboard = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }

    navigate("/dashboard");
  };

  const handleOpenTimelineSource = (entry = {}) => {
    if (!entry?.id) {
      return;
    }
    setSelectedSourceEntry(entry);
  };

  const handleCloseSourceEntry = () => {
    setSelectedSourceEntry(null);
  };

  const handleLogSomething = () => {
    if (child && typeof onLogSomething === "function") {
      onLogSomething(child);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background: `radial-gradient(circle at top left, ${alpha(colors.brand.ink, 0.08)} 0%, transparent 28%), linear-gradient(180deg, ${alpha(colors.brand.ice, 0.58)} 0%, ${theme.palette.background.paper} 100%)`,
        display: "flex",
        flexDirection: "column",
        pb: { xs: "calc(5.8rem + env(safe-area-inset-bottom, 0px))", md: 3 },
      }}
    >
      <DailyCareReportHeader
        childName={childName}
        periodLabel={periodLabel}
        rangePreset={rangePreset}
        setRangePreset={setRangePreset}
        customStartDate={customStartDate}
        setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}
        setCustomEndDate={setCustomEndDate}
        onBack={handleBackToDashboard}
        onDownloadPdf={handleDownloadPdf}
        onCopy={handleCopy}
        onShare={handleShare}
        onOpenFilters={() => setFilterPanelOpen(true)}
        theme={theme}
      />

      <DailyCareReportContent
        feedback={feedback}
        loading={loading}
        isCustomRangeInvalid={isCustomRangeInvalid}
        filteredEntries={filteredEntries}
        childName={childName}
        periodLabel={periodLabel}
        rangePreset={rangePreset}
        onLogSomething={handleLogSomething}
        summaryData={summaryData}
        summaryLoading={summaryLoading}
        questionsToDiscuss={questionsToDiscuss}
        keyEvents={keyEvents}
        peopleLoggedCount={peopleLoggedCount}
        dateSections={dateSections}
        onDownloadPdf={handleDownloadPdf}
        onCopy={handleCopy}
        onShare={handleShare}
        onOpenTimelineSource={handleOpenTimelineSource}
        theme={theme}
      />

      <DailyCareReportFiltersDrawer
        isMobile={isMobile}
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        selectedFilters={selectedFilters}
        toggleFilter={toggleFilter}
        clearFilters={clearFilters}
        selectAllFilters={selectAllFilters}
        theme={theme}
      />
      <TherapyPrepSourceEntrySheet
        open={Boolean(selectedSourceEntry)}
        entry={selectedSourceEntry}
        onClose={handleCloseSourceEntry}
        accent={childAccent}
        theme={theme}
      />
    </Box>
  );
};

export default DailyCareReport;
